import React, { useEffect, useMemo, useState } from 'react';
import { apv as api } from '../services/api';
import { nb, eurCourt, pct1, signePct, CelluleDelta, useFiltresVentes, PanneauVentes, usePanneau, basculer } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './VentesJour.css';  // rg-* du regroupement
import './Ventes.css';
import './Marche.css';   // bouton « Analyse IA » (mk-ia, mk-tete-d)
import ApvAnalyseIa from './ApvAnalyseIa';
import { useBi } from '../BiContext';

const ATELIERS = ['Mécanique', 'Rapide', 'Carrosserie', 'Préparation VN/VO', 'Magasin'];
const kEur = (v) => (v == null ? '—' : (v / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) + ' k€');

function Delta({ pct, pt, ref = 'N-1' }) {
  const val = pct != null ? pct : pt;
  if (val == null) return <div className="d"><small>—</small></div>;
  const pos = val >= 0;
  return <div className={`d ${pos ? 'pos' : 'neg'}`}>{pt != null ? `${pos ? '+' : ''}${pct1(pt).replace(' %', ' €')}` : signePct(pct)}<small> vs {ref}</small></div>;
}

// `branche` choisit le mart côté serveur (Antilles par défaut, Auto Métropole
// pour am-apv-performance). En métropole : un seul calendrier (pas de badge
// d'île ni de sous-total, le CA/jour va sur la ligne Total) et pas de budget.
export default function ApvPerformance({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  // Analyse IA (2026-10-04) : Auto Antilles seulement, sur la période du rapport.
  const [ia, setIa] = useState(false);
  const { ia: droitIa } = useBi(); // droit « Analyse IA » de la personne connectée
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const atelier = f.params.get('atelier') || '';

  useEffect(() => {
    let actif = true; setErreur('');
    api.performance({ periode: f.periode, mois: f.moisStr, societes: f.societesUrl, atelier, branche })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche, f.periode, f.moisStr, f.societesUrl, atelier]);

  // Maille du bloc « Par société » : concession, site, plaque. Cliquer une
  // ligne filtre toute la page, regroupement compris.
  // CA/jour d'un regroupement = SOMME des CA/jour de ses membres, chacun
  // divisé par les jours ouvrés de son île (2026-09-25, correction du user :
  // « c'est une addition »). Sur une seule île, c'est CA cumulé ÷ jours ; sur
  // deux îles aux calendriers différents, ça reste le CA moyen produit par
  // jour ouvré, là où un diviseur commun n'existe pas.
  const maille = useRegroupement();
  const feuilles = useMemo(
    () => (data?.parSociete || []).map((s) => ({ ...s, libelle: s.nom, code: s.code })), [data]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const sommerSoc = (membres) => {
    const som = (f) => membres.reduce((t, m) => t + (f(m) || 0), 0);
    const ca = som((m) => m.ca.valeur), caN1 = som((m) => m.ca.n1);
    const h = som((m) => m.heures.valeur), hN1 = som((m) => m.heures.n1);
    const iles = new Set(membres.map((m) => m.ile));
    const jours = iles.size === 1 ? membres[0].jours : null;
    const pct = (a, b) => (b ? ((a - b) / Math.abs(b)) * 100 : null);
    const th = h ? ca / h : null, thN1 = hN1 ? caN1 / hN1 : null;
    const somme = (f) => (membres.some((m) => f(m) != null) ? som(f) : null);
    const cj = somme((m) => m.caJour.valeur), cjN1 = somme((m) => m.caJour.n1);
    return {
      ile: iles.size === 1 ? [...iles][0] : '—', jours,
      ca: { valeur: ca, n1: caN1, deltaPct: pct(ca, caN1) },
      caJour: { valeur: cj, n1: cjN1, deltaPct: pct(cj, cjN1) },
      heures: { valeur: h, n1: hN1, deltaPct: pct(h, hN1) },
      tauxHoraire: { valeur: th, n1: thN1, deltaPt: (th != null && thN1 != null) ? th - thN1 : null },
    };
  };
  const arbreSocietes = useMemo(
    () => construireArbre(feuilles, maille.niveau, sommerSoc, maille.ouverts, (x) => x.ca?.valeur),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);

  const Panneau = <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo} />;
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const k = data.kpis;
  const budgetOk = data.budgetDisponible !== false;
  const multiIle = (data.caJourIles || []).length > 1;
  const uneIle = !multiIle && (data.caJourIles || [])[0];
  // Total multi-île : même règle que les regroupements, la somme des CA/jour d'île.
  const sommeIles = (c) => ((data.caJourIles || []).some((x) => x[c] != null)
    ? data.caJourIles.reduce((t, x) => t + (x[c] || 0), 0) : null);
  const totCaJour = multiIle ? { caJour: sommeIles('caJour'), caJourN1: sommeIles('caJourN1') } : uneIle;

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div>
            <h1>APV — Performance</h1>
          </div>
          <div className="mk-tete-d">
            {branche === 'auto-antilles' && droitIa !== 'aucune' && (
              <button type="button" className="mk-ia" onClick={() => setIa(true)}
                title="Analyse IA de l'atelier : heures vendues, taux horaire, mix, objectifs, OR en cours, pistes d'action — concessions pseudonymisées">Analyse IA</button>
            )}
            <div className="vt-asof"><b>{data.periode}</b></div>
          </div>
        </div>

        <div className="vt-ctl">
          <span><span className="lab">Atelier</span>
            <span className="vt-seg">
              <button className={!atelier ? 'on' : ''} onClick={() => f.setFiltre({ atelier: '' })}>Tous</button>
              {(data.ateliersDispo || ATELIERS).map((a) => <button key={a} className={atelier === a ? 'on' : ''} onClick={() => f.setFiltre({ atelier: a })}>{a}</button>)}
            </span>
          </span>
        </div>

        {data.vide ? (
          <div className="bi-empty" style={{ marginTop: 20 }}><h2>Aucune donnée</h2><p>Ajustez les filtres (société, atelier, période).</p></div>
        ) : (
          <>
            <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
              <div className="vt-kpi"><div className="l">CA main d'œuvre</div><div className="v">{eurCourt(k.ca.valeur)}</div>
                <Delta pct={k.ca.deltaPct} />
                {budgetOk && <div className={`d ${(k.ca.deltaBudPct || 0) >= 0 ? 'pos' : 'neg'}`} style={{ marginTop: 1 }}>{signePct(k.ca.deltaBudPct)}<small> vs Budget</small></div>}</div>
              <div className="vt-kpi"><div className="l">Heures vendues</div><div className="v">{nb(k.heures.valeur)}</div><Delta pct={k.heures.deltaPct} /></div>
              <div className="vt-kpi"><div className="l">Taux horaire</div><div className="v">{pct1(k.tauxHoraire.valeur).replace(' %', ' €')}</div><Delta pt={k.tauxHoraire.deltaPt} /></div>
              <div className="vt-kpi"><div className="l">Nb factures</div><div className="v">{nb(k.nbFactures.valeur)}</div><div className="d"><small>{nb(k.nbOr.valeur)} OR</small></div></div>
            </div>

            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Par société</h2>
                <div className="st-tools">
                  <span className="hint">CA/jour = CA ÷ jours ouvrés de l'île ; un regroupement additionne ceux de ses membres</span>
                  <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                </div>
              </div>
              <div className="vt-card-b vt-tbl-wrap">
              <table className="vt-tbl">
                <thead><tr>
                  <th className="l">Société</th>
                  <th>CA HT</th><th className="vt-dh">vs N-1</th>
                  <th>CA / jour</th><th className="vt-dh">vs N-1</th>
                  <th>Taux horaire</th><th className="vt-dh">vs N-1</th>
                </tr></thead>
                <tbody>
                  {arbreSocietes.map((s) => {
                    const cible = s.feuille ? s.code : (s.membres || []).join(',');
                    return (
                    <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
                        onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
                      <td className="l">
                        <span className="rg-in" style={{ width: s.niveau * 14 }} />
                        {s.feuille ? <span className="rg-chev vide" /> : (
                          <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`}
                            title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par société'}
                            aria-label="Déplier"
                            onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
                        )}
                        {s.libelle || s.nom}
                        {!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
                        <small style={{ marginLeft: 6, fontWeight: 500, color: 'var(--text-muted,#96a0b0)' }}>{multiIle ? `${s.ile} · ` : ''}{s.jours == null ? '' : `${nb(s.jours)} j`}</small>
                      </td>
                      <td>{eurCourt(s.ca.valeur)}</td><CelluleDelta valeur={s.ca.valeur} n1={s.ca.n1} fmt={eurCourt} />
                      <td>{kEur(s.caJour.valeur)}</td><CelluleDelta valeur={s.caJour.valeur} n1={s.caJour.n1} fmt={kEur} />
                      <td className="vt-taux">{pct1(s.tauxHoraire.valeur).replace(' %', ' €')}</td><CelluleDelta pt={s.tauxHoraire.deltaPt} suffixe=" €" />
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {/* Plus de sous-totaux par île (retirés le 2026-09-25) : la
                      maille « Plaque » donne déjà cette vue. */}
                  <tr>
                    <td className="l">Total{uneIle ? <small style={{ marginLeft: 6, fontWeight: 500, color: 'var(--text-muted,#96a0b0)' }}>{nb(uneIle.jours)} j</small> : null}</td>
                    <td>{eurCourt(k.ca.valeur)}</td><CelluleDelta valeur={k.ca.valeur} n1={k.ca.n1} fmt={eurCourt} />
                    {totCaJour ? <><td>{kEur(totCaJour.caJour)}</td><CelluleDelta valeur={totCaJour.caJour} n1={totCaJour.caJourN1} fmt={kEur} /></> : <><td>—</td><td className="vt-d" /></>}
                    <td>{pct1(k.tauxHoraire.valeur).replace(' %', ' €')}</td><CelluleDelta pt={k.tauxHoraire.deltaPt} suffixe=" €" />
                  </tr>
                </tfoot>
              </table>
            </div></div>

            <div className="vt-card">
              <div className="vt-card-h"><h2>Analyse par atelier</h2></div>
              <div className="vt-card-b vt-tbl-wrap">
              <table className="vt-tbl">
                <thead><tr><th className="l">Atelier</th><th>CA HT</th><th>CA / jour</th><th>Heures</th><th>Taux horaire</th></tr></thead>
                <tbody>
                  {data.parAtelier.map((a) => (
                    <tr key={a.atelier}>
                      <td className="l">{a.atelier}</td>
                      <td>{eurCourt(a.ca)}</td>
                      <td>{kEur(a.caJour)}</td>
                      <td>{nb(a.heures)}</td>
                      <td className="vt-taux">{pct1(a.tauxHoraire).replace(' %', ' €')}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td className="l">Total</td><td>{eurCourt(k.ca.valeur)}</td><td>{totCaJour ? kEur(totCaJour.caJour) : '—'}</td><td>{nb(k.heures.valeur)}</td><td>{pct1(k.tauxHoraire.valeur).replace(' %', ' €')}</td></tr></tfoot>
              </table>
            </div></div>

          </>
        )}
      </main>
      {Panneau}
      {ia && <ApvAnalyseIa selection={f.selected} filtres={{ periode: f.periode, mois: f.moisStr }} libellePeriode={data.periode} onFermer={() => setIa(false)} />}
    </>
  );
}
